import { token } from '@atlaskit/tokens';
import { useStatsig } from '@atlassian/mpac-feature-flags';
import { styled } from '@compiled/react';
import React from 'react';
import { Link } from 'react-router-dom';

import { TEST_IDS } from '../../../../common/testIds';
import { Text, TextWC } from './Button.styles';

const MenuLinkContainer = styled.div({
  display: 'flex',
  alignItems: 'center',
  height: '100%',
  position: 'relative',
});

const MenuLink = styled(Link)({
  display: 'flex',
  alignItems: 'center',
  backgroundColor: token('color.background.neutral.subtle'),
  cursor: 'pointer',
  color: token('color.text.subtle'),
  justifyContent: 'center',
  borderRadius: '3px',
  border: 'none',
  whiteSpace: 'nowrap',
  padding: `${token('space.050')} ${token('space.075')}`,
  gap: token('space.075'),
  textDecoration: 'none',

  '&:hover': {
    backgroundColor: token('color.background.neutral.hovered'),
    color: token('color.text.subtle'),
    textDecoration: 'none',
  },
  '&:active': {
    backgroundColor: token('color.background.neutral.pressed'),
    color: token('color.text.subtle'),
  },
  '&:focus-visible': {
    outline: `2px solid ${token('color.border.focused')}`,
  },
});

const MenuLinkContainerWC = styled.div({
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
});

const MenuLinkWC = styled(Link)({
  display: 'inline-flex',
  alignItems: 'center',
  cursor: 'pointer',
  justifyContent: 'center',
  borderRadius: '3px',
  border: 'none',
  whiteSpace: 'nowrap',
  padding: `${token('space.100')} ${token('space.200')}`,
  gap: token('space.100'),
  backgroundColor: 'transparent',
  color: token('color.text'),
  textDecoration: 'none',

  '&:hover': {
    color: token('color.text.selected'),
    backgroundColor: 'transparent',
    textDecoration: 'none',
  },
  '&:active': {
    color: token('color.text.selected'),
    backgroundColor: 'transparent',
  },
  '&:focus-visible': {
    outline: `2px solid ${token('color.border.focused')}`,
  },
});

interface MenuLinkDesktopProps {
  to: string;
  label: string;
}

export const MenuLinkDesktop = ({ to, label }: MenuLinkDesktopProps) => {
  const { enableMpacWebCoherence } = useStatsig();

  return enableMpacWebCoherence() ? (
    <MenuLinkContainerWC>
      <MenuLinkWC to={to} aria-label={label} data-testId={TEST_IDS.MENU_LINK_DESKTOP}>
        <TextWC>{label}</TextWC>
      </MenuLinkWC>
    </MenuLinkContainerWC>
  ) : (
    <MenuLinkContainer>
      <MenuLink to={to} aria-label={label} data-testId={TEST_IDS.MENU_LINK_DESKTOP}>
        <Text>{label}</Text>
      </MenuLink>
    </MenuLinkContainer>
  );
};
